import { inject, Injectable } from '@angular/core';
import { LoggableAPI } from '../models/protocols/loggable-api';
import { ApiClient } from './api-client';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { Observable, throwError } from 'rxjs';
import { ODataResponse } from '../models/protocols/odata-response';
import { Endpoints } from './endpoints';
import { catchError, map } from 'rxjs/operators';
import { CustomError } from '../models/shared/custom-error';
import { MemberSubmission, SubmissionStakeholder } from '../models/submissions/member-submission';
import { ChangeNameRequest } from '../models/users/requests/submissionRequests/change-name-request';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { SubmissionCreated } from '../models/submissions/submission-created';
import { SubmissionCommentRequest } from '../models/users/requests/submission-comment-request';
import { SubmissionComment } from '../models/submissions/submission-comment';
import { SubmissionStatusCommentRequest } from '../models/submissions/requests/submission-comment-request';
import { InternalUserCommentReadReceiptRequest } from '../models/users/requests/internal-user-comment-read-receipt-request';
import { HttpResponse } from '@angular/common/http';
import { SubmissionSubStatusEnum } from '../models/enum/shared/submission-sub-status.enum';
import { UpdateSubmissionOwnerRequest } from '../models/users/requests/update-submission-owner-request';
import { UpdateSubmissionApproverRequest } from '../models/users/requests/update-submission-approver-request';
import { SubmissionRoleTemplate } from '../models/submissions/submission-role-template';
import { MemberSubmissionType } from '../models/submissions/member-submission-type';
import { SubmissionPermissionType } from '../models/permissions/dto/submission-permission-type';
import { SubmissionPermissionWithType } from '../models/permissions/dto/submission-permission-with-type';
import { ChangeAddressRequest } from '../models/users/requests/submissionRequests/change-address-request';
import { MemberSubmissionReminderRequest } from '../models/users/requests/member-submission-reminder-request';
import { UpdateContactInfoRequest } from '../models/users/requests/submissionRequests/update-contact-info-request';
import { ChangePersonalInfoRequest } from '../models/users/requests/submissionRequests/change-personal-info-request';
import { ChangeInvestmentInstructionSubmissionRequest } from '../models/users/requests/change-investment-instruction-submission-request';
import { RecentMemberSubmissionResponse } from '../models/submissions/recent-member-submission-response';
import { RelationshipStatusSubmissionRequest } from '../models/submissions/requests/relationship-status-submission-request';
import { RelationshipSubmissionApprovalRequest } from '../models/submissions/requests/relationship-submission-approval-request';
import { UpdateBeneficiarySpousalWaiverRequest } from '../models/submissions/requests/update-beneficiary-spousal-waiver-request';
import { MpBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/mp-beneficiary-change-submission-request';
import { IpBeneficiaryChangeSubmissionRequest } from '../models/submissions/requests/ip-beneficiary-change-submission-request';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { MpCreateTransferInSubmissionRequest } from '../features/transfer-in-request/models/mp-create-transfer-in-submission-request';
import { IpCreateTransferInSubmissionRequest } from '../features/transfer-in-request/models/ip-create-transfer-in-submission-request';
import { IpUpdateTransferInTransferRecordRequest } from '../features/transfer-in-request/models/ip-update-transfer-in-transfer-record-request';
import { CreateBankingInformationSubmissionRequest } from '../models/submissions/requests/create-banking-information-submission-request';
import { CreateLegalFiduciarySubmissionRequest } from '../models/users/requests/submissionRequests/create-legal-fiduciary-submission-request';
import { SharedSubmission } from '../models/submissions/shared-submission';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { UpdateSubmissionStatusRequest } from '../models/submissions/requests/update-submission-status-request';
import { EmployerSubmissionReminderRequest } from '../models/users/requests/employer-submission-reminder-request';
import { EpEmployerSubmission } from '../models/submissions/employer-submissions/ep-employer-submission';
import { EpUpdateEmployerAddressRequest } from '../models/users/requests/submissionRequests/ep-update-employer-address-request';
import { EmployerSubmissionType } from '../models/submissions/employer-submission-type';
import { CreateVariableBenefitConfigurationRequest } from '../models/submissions/requests/create-variable-benefit-configuration-request';
import { UpdateVariableBenefitConfigurationSubmissionRequest } from '../models/submissions/requests/update-variable-benefit-configuration-submission-request';
import { IpVariableBenefitPaymentChangeSubmissionRequest } from '../models/submissions/requests/ip-variable-benefit-payment-change-submission-request';
import { MpVariableBenefitPaymentChangeSubmissionRequest } from '../models/submissions/requests/mp-variable-benefit-payment-change-submission-request';
import { MpRetirementProductSubmissionAvailability } from '../models/submissions/mp-retirement-product-submission-availability';
import { IpRetirementProductSubmissionAvailability } from '../models/submissions/ip-retirement-product-submission-availability';
import { CreateMonthlyPensionSetupRequest } from '../views/records/components/members/ip-member-record/components/ip-member-record-create-monthly-pension-account/requests/monthly-pension-setup/create-monthly-pension-setup-request';
import { IpCreatePaylineAccountRequest } from '../models/users/requests/submissionRequests/ip-create-payline-account-request';

@Injectable({
  providedIn: 'root'
})
export class SubmissionsAPI implements LoggableAPI {
  constructor() {}

  private apiClient = inject(ApiClient);

  public serviceName = 'Submissions';

  public ipGetMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    const url = Endpoints.ipGetMemberSubmissions();
    oDataQueryOptions.setExpand(
      'Status',
      'SubStatus',
      'Owner($expand=ProfileBadgeConfiguration)',
      'Approver($expand=ProfileBadgeConfiguration)',
      'Reminder'
    );
    return this.apiClient.getOdata<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<SharedSubmission>> {
    const url = Endpoints.ipGetSharedSubmissions();
    return this.apiClient.getOdata(url, SharedSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    const url = Endpoints.mpGetMemberSubmissions();
    oDataQueryOptions.setExpand('Status');
    return this.apiClient.getOdata<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionById(id: string): Observable<MemberSubmission> {
    const url = Endpoints.ipGetMemberSubmissionById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Status',
      'SubStatus',
      'Owner($expand=ProfileBadgeConfiguration)',
      'Approver($expand=ProfileBadgeConfiguration)',
      'Reminder',
      'Attachments($expand=Attachment)',
      'History',
      'BeneficiaryChangeSubmission($expand=Previous($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))),New($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))), SubmissionSpousalWaivers($expand=Attachment, GoverningJurisdiction))',
      'Comments($expand=Author($expand=ProfileBadgeConfiguration))',
      'NameChangeSubmission',
      'AddressChangeSubmission($expand=Previous($expand=State,Country),New($expand=State,Country))',
      'ContactInformationSubmission',
      'PersonalInformationSubmission',
      'InvestmentInstructionSubmission($expand=AccountType, Previous($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)), New($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)))',
      'RelationshipStatusSubmission($expand=Previous($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))), New($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))))',
      'BankingInformationSubmission',
      'LegalFiduciarySubmission($expand=Previous($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))), New($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))))',
      'VariableBenefitConfigurationSubmission($expand=New($expand=RecurringPaymentFrequency, PaymentMethod, BankAccount, PaylineAccount($expand=Currency), WithdrawalInstructions($expand=Fund), GoverningJurisdictionOverride, SourceJurisdictions($expand=GoverningJurisdiction), DestinationJurisdictions($expand=GoverningJurisdiction, SpousalWaivers($expand=WaiverType, Attachment))))',
      'UpdateVariableBenefitConfigurationSubmission($expand=Previous($expand=RecurringPaymentFrequency, DestinationJurisdictions($expand=GoverningJurisdiction)), New($expand=RecurringPaymentFrequency, GoverningJurisdictionOverride, SourceJurisdictions($expand=GoverningJurisdiction), DestinationJurisdictions($expand=GoverningJurisdiction, SubmissionSpousalWaivers($expand=WaiverType, Attachment), MemberSpousalWaivers($expand=WaiverType, Attachment))))',
      'VariableBenefitPaymentChangeSubmission($expand=Previous($expand=RecurringPaymentFrequency, BankAccount, PaymentMethod, PaylineAccount($expand=Currency), JurisdictionDetails($expand=GoverningJurisdiction), WithdrawalInstructions($expand=Fund)), New($expand=RecurringPaymentFrequency, PaymentMethod, BankAccount, PaylineAccount($expand=Currency), JurisdictionDetails($expand=GoverningJurisdiction), WithdrawalInstructions($expand=Fund)))',
      'MonthlyPensionSetupSubmission($expand=New($expand=AnnuityType,AnnuityGuaranteePeriod,PaymentMethod,BankAccount,PaylineAccount($expand=Currency),GoverningJurisdictionOverride,JurisdictionDetails($expand=GoverningJurisdiction),SpousalWaivers($expand=WaiverType,Attachment,GoverningJurisdiction)))',
      'PaylineAccountSubmission($expand=New($expand=Currency))',
      'TransferInSubmission($expand=New($expand=InstitutionAddress,FinancialAdvisor),TransferRecord)'
    );
    return this.apiClient.getOdataObj<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetSubmissionById(id: string): Observable<MemberSubmission> {
    const url = Endpoints.mpGetSubmissionById(id);
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand(
      'Status',
      'Attachments($expand=Attachment)',
      'Comments($expand=Author($expand=ProfileBadgeConfiguration))',
      'NameChangeSubmission',
      'BeneficiaryChangeSubmission($expand=Previous($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))),New($expand=Beneficiaries($expand=Type, RelationshipToMember, Address($expand=State,Country))), SubmissionSpousalWaivers($expand=Attachment, GoverningJurisdiction))',
      'AddressChangeSubmission($expand=Previous($expand=State,Country),New($expand=State,Country))',
      'ContactInformationSubmission',
      'PersonalInformationSubmission',
      'InvestmentInstructionSubmission($expand=accountType, Previous($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)), New($expand=InvestmentInstructions($expand=Fund), RebalancingInstructions($expand=Fund)))',
      'RelationshipStatusSubmission($expand=Previous($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))), New($expand=RelationshipStatus, Spouse($expand=Address($expand=State, Country))))',
      'BankingInformationSubmission',
      'LegalFiduciarySubmission($expand=Previous($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))), New($expand=Type, EntityType, PowerOfAttorneyType, PowerOfAttorneyAppointmentType, OrganizationEntity($expand=Address($expand=State, Country)), IndividualEntities($expand=Address($expand=State, Country))))',
      'VariableBenefitConfigurationSubmission($expand=New($expand=RecurringPaymentFrequency, PaymentMethod, BankAccount, PaylineAccount($expand=Currency), WithdrawalInstructions($expand=Fund), GoverningJurisdictionOverride, SourceJurisdictions($expand=GoverningJurisdiction), DestinationJurisdictions($expand=GoverningJurisdiction, SpousalWaivers($expand=WaiverType, Attachment))))',
      'UpdateVariableBenefitConfigurationSubmission($expand=Previous($expand=RecurringPaymentFrequency, DestinationJurisdictions($expand=GoverningJurisdiction)), New($expand=RecurringPaymentFrequency, GoverningJurisdictionOverride, SourceJurisdictions($expand=GoverningJurisdiction), DestinationJurisdictions($expand=GoverningJurisdiction, SubmissionSpousalWaivers($expand=WaiverType, Attachment), MemberSpousalWaivers($expand=WaiverType, Attachment))))',
      'VariableBenefitPaymentChangeSubmission($expand=Previous($expand=RecurringPaymentFrequency, BankAccount, PaymentMethod, PaylineAccount($expand=Currency), JurisdictionDetails($expand=GoverningJurisdiction), WithdrawalInstructions($expand=Fund)), New($expand=RecurringPaymentFrequency, PaymentMethod, BankAccount, PaylineAccount($expand=Currency), JurisdictionDetails($expand=GoverningJurisdiction), WithdrawalInstructions($expand=Fund)))',
      'MonthlyPensionSetupSubmission($expand=New($expand=AnnuityType,AnnuityGuaranteePeriod,PaymentMethod,BankAccount,PaylineAccount($expand=Currency),GoverningJurisdictionOverride,JurisdictionDetails($expand=GoverningJurisdiction),SpousalWaivers($expand=WaiverType,Attachment,GoverningJurisdiction)))',
      'PaylineAccountSubmission($expand=New($expand=Currency))',
      'TransferInSubmission($expand=New($expand=InstitutionAddress,FinancialAdvisor))'
    );
    return this.apiClient.getOdataObj<MemberSubmission>(url, MemberSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissions(oDataQueryOptions: ODataQueryOptions) {
    const url = Endpoints.ipGetEmployerSubmissions();
    oDataQueryOptions.setExpand(
      'Status',
      'SubStatus',
      'Owner($expand=ProfileBadgeConfiguration)',
      'Approver($expand=ProfileBadgeConfiguration)',
      'Reminder'
    );
    return this.apiClient.getOdata<IpEmployerSubmission>(url, IpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerSubmissions(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<EpEmployerSubmission>> {
    const url = Endpoints.epGetEmployerSubmissions();
    oDataQueryOptions.setExpand('Status');
    return this.apiClient.getOdata<EpEmployerSubmission>(url, EpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epGetEmployerSubmissionById(
    id: string,
    oDataQueryOptions: ODataQueryOptions
  ): Observable<EpEmployerSubmission> {
    const url = Endpoints.epGetEmployerSubmissionById(id);
    return this.apiClient.getOdataObj<EpEmployerSubmission>(url, EpEmployerSubmission, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public epUpdateEmployerAddress(req: EpUpdateEmployerAddressRequest): Observable<EpEmployerSubmission> {
    const url = Endpoints.epUpdateEmployerAddress();
    return this.apiClient.postObj(EpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionById(id: string, queryOptions: ODataQueryOptions): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipGetEmployerSubmissionById(id);
    return this.apiClient.getOdataObj<IpEmployerSubmission>(url, IpEmployerSubmission, queryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionViewersForMemberSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionViewersForMemberSubmissionType();
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('ProfileBadgeConfiguration');
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetEmployerSubmissionViewersForEmployerSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionViewersForEmployerSubmissionType();
    const oDataQueryOptions = new ODataQueryOptions();
    oDataQueryOptions.setExpand('ProfileBadgeConfiguration');
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, null, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public createInternalNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalNameChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createNameChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalPersonalInfoChangeSubmission(req: ChangePersonalInfoRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalPersonalInfoSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createPersonalInfoChangeSubmission(req: ChangePersonalInfoRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createPersonalInfoSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalAddressChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createAddressChangeSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalUpdateContactInformationSubmission(
    req: UpdateContactInfoRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalUpdateContactInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createUpdateContactInformationSubmission(req: UpdateContactInfoRequest): Observable<SubmissionCreated> {
    const url = Endpoints.createUpdateContactInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<SubmissionAttachment> {
    const url = Endpoints.ipCreateMemberSubmissionAttachment(submissionId);
    return this.apiClient.postObj(SubmissionAttachment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<SubmissionAttachment> {
    const url = Endpoints.ipCreateEmployerSubmissionAttachment(submissionId);
    return this.apiClient.postObj(SubmissionAttachment, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createInternalMemberInvestmentInstructionsSubmission(
    req: ChangeInvestmentInstructionSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.createInternalMemberInvestmentInstructionsSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public createMemberInvestmentInstructionsSubmission(
    req: ChangeInvestmentInstructionSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.createMemberInvestmentInstructionsSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateRelationshipStatusSubmission(req: RelationshipStatusSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateRelationshipStatusSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateRelationshipStatusSubmission(req: RelationshipStatusSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateRelationshipStatusSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateBeneficiaryUpdateSubmission(
    req: MpBeneficiaryChangeSubmissionRequest,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberSubmission> {
    let url = Endpoints.mpCreateDCBeneficiaryUpdateSubmission();
    switch (accountTypeId) {
      case AccountTypeIdEnum.VariableBenefit:
        url = Endpoints.mpCreateVBBeneficiaryUpdateSubmission();
        break;
      case AccountTypeIdEnum.MonthlyPension:
        url = Endpoints.mpCreateMonthlyPensionBeneficiaryUpdateSubmission();
        break;
    }
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateBeneficiaryUpdateSubmission(
    req: IpBeneficiaryChangeSubmissionRequest,
    accountTypeId: AccountTypeIdEnum
  ): Observable<MemberSubmission> {
    let url = Endpoints.ipCreateDCBeneficiaryUpdateSubmission();
    switch (accountTypeId) {
      case AccountTypeIdEnum.VariableBenefit:
        url = Endpoints.ipCreateVBBeneficiaryUpdateSubmission();
        break;
      case AccountTypeIdEnum.MonthlyPension:
        url = Endpoints.ipCreateMonthlyPensionBeneficiaryUpdateSubmission();
        break;
    }
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionStatusWithComment(
    req: SubmissionStatusCommentRequest,
    url: string
  ): Observable<MemberSubmission> {
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateEmployerSubmissionStatusWithComment(
    req: SubmissionStatusCommentRequest,
    url: string
  ): Observable<IpEmployerSubmission> {
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionStatus(
    status: SubmissionSubStatusEnum,
    submissionId: string
  ): Observable<MemberSubmission> {
    let endpoint = '';
    switch (status) {
      case SubmissionSubStatusEnum.Processing:
        endpoint = Endpoints.ipSetMemberSubmissionToProcessing(submissionId);
        break;
      case SubmissionSubStatusEnum.Approved:
        endpoint = Endpoints.ipSetMemberSubmissionToApproved(submissionId);
        break;
    }
    const req = new UpdateSubmissionStatusRequest(+submissionId);
    return this.apiClient.putObj(MemberSubmission, endpoint, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionStatus(
    status: SubmissionSubStatusEnum,
    submissionId: string
  ): Observable<IpEmployerSubmission> {
    let endpoint = '';
    switch (status) {
      case SubmissionSubStatusEnum.Processing:
        endpoint = Endpoints.ipSetEmployerSubmissionToProcessing(submissionId);
        break;
      case SubmissionSubStatusEnum.Approved:
        endpoint = Endpoints.ipSetEmployerSubmissionToApproved(submissionId);
        break;
    }
    const req = new UpdateSubmissionStatusRequest(+submissionId);
    return this.apiClient.putObj(IpEmployerSubmission, endpoint, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public updateRelationshipSubmissionStatusToApproved(
    req: RelationshipSubmissionApprovalRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSetRelationshipStatusSubmissionToApproved(req.id);
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateBeneficiarySpousalWaiver(req: UpdateBeneficiarySpousalWaiverRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateBeneficiarySpousalWaiver(req.submissionId.toString(10), req.spousalWaiverId);
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddMemberSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    const url = Endpoints.ipAddMemberSubmissionComment(req.submissionId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(SubmissionComment, res.body);
        return comments.sort((a, b) => {
          // @ts-ignore
          return new Date(b.createdDate) - new Date(a.createdDate);
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipAddEmployerSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    const url = Endpoints.ipAddEmployerSubmissionComment(req.submissionId);
    return this.apiClient.simplePost(url, req).pipe(
      map(res => {
        const comments = window?.injector?.Deserialize?.arrayOf(SubmissionComment, res.body);
        return comments.sort((a, b) => {
          // @ts-ignore
          return new Date(b.createdDate) - new Date(a.createdDate);
        });
      }),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetMemberSubmissionCommentsAsRead(
    req: InternalUserCommentReadReceiptRequest
  ): Observable<HttpResponse<any>> {
    const url = Endpoints.ipSetMemberSubmissionCommentsAsRead();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetEmployerSubmissionCommentsAsRead(
    req: InternalUserCommentReadReceiptRequest
  ): Observable<HttpResponse<any>> {
    const url = Endpoints.ipSetEmployerSubmissionCommentsAsRead();
    return this.apiClient.simplePost(url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissionApprovers(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetSharedSubmissionApprovers();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionApproversForSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionApprovers();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetEmployerSubmissionApproversForSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionApprovers();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetMemberSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSharedSubmissionOwners(
    oDataQueryOptions: ODataQueryOptions
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetSharedSubmissionOwners();
    return this.apiClient.getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberSubmissionOwnersForSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetMemberSubmissionOwners();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public ipGetEmployerSubmissionOwnersForSubmissionType(
    submissionTypeId: string
  ): Observable<ODataResponse<SubmissionStakeholder>> {
    const url = Endpoints.ipGetEmployerSubmissionOwners();
    const oDataQueryOptions = new ODataQueryOptions();
    const additionalParams = `submissionTypeId=${submissionTypeId}`;
    return this.apiClient
      .getOdata<SubmissionStakeholder>(url, SubmissionStakeholder, oDataQueryOptions, additionalParams)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getSubmissionRoleTemplates(): Observable<ODataResponse<SubmissionRoleTemplate>> {
    const url = Endpoints.getSubmissionRoleTemplates();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<SubmissionRoleTemplate>(url, SubmissionRoleTemplate, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getSubmissionPermissionTypes(): Observable<SubmissionPermissionType[]> {
    const url = Endpoints.getSubmissionPermissionTypes();
    return this.apiClient.getArr<SubmissionPermissionType>(SubmissionPermissionType, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getAllSubmissionPermissions(): Observable<ODataResponse<SubmissionPermissionWithType>> {
    const url = Endpoints.getAllSubmissionPermissions();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient
      .getOdata<SubmissionPermissionWithType>(url, SubmissionPermissionWithType, oDataQueryOptions)
      .pipe(
        catchError(e => {
          const err = new CustomError(e, this.serviceName);
          return throwError(() => err);
        })
      );
  }

  public getInternalMemberSubmissionTypes(): Observable<ODataResponse<MemberSubmissionType>> {
    const url = Endpoints.ipGetMemberSubmissionTypes();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<MemberSubmissionType>(url, MemberSubmissionType, oDataQueryOptions).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerSubmissionTypes(): Observable<EmployerSubmissionType[]> {
    const url = Endpoints.ipGetEmployerSubmissionTypes();
    const oDataQueryOptions = new ODataQueryOptions();
    return this.apiClient.getOdata<EmployerSubmissionType>(url, EmployerSubmissionType, oDataQueryOptions).pipe(
      map(res => res.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateMemberSubmissionOwner(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerSubmissionOwner(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateMemberSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateMemberSubmissionApprover(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateEmployerSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipUpdateEmployerSubmissionApprover(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetMemberSubmissionToReadyForApproval(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipSetMemberSubmissionToReadyForApproval(req.id.toString(10));
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSetEmployerSubmissionToReadyForApproval(
    req: UpdateSubmissionApproverRequest
  ): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipSetEmployerSubmissionToReadyForApproval(req.id.toString(10));
    return this.apiClient.putObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateMemberSubmissionReminder(req: MemberSubmissionReminderRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipCreateMemberSubmissionReminder(req.memberSubmissionId);
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateEmployerSubmissionReminder(req: EmployerSubmissionReminderRequest): Observable<IpEmployerSubmission> {
    const url = Endpoints.ipCreateEmployerSubmissionReminder(req.employerSubmissionId);
    return this.apiClient.postObj(IpEmployerSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteMemberSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteMemberSubmissionReminder(submissionId, reminderId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteEmployerSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteEmployerSubmissionReminder(submissionId, reminderId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public getInternalMemberRecentSubmissions(memberId: number): Observable<RecentMemberSubmissionResponse> {
    const url = Endpoints.ipGetMemberRecentSubmissions(memberId);
    return this.apiClient.getObj(RecentMemberSubmissionResponse, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetEmployerRecentSubmissions(employerId: number): Observable<RecentMemberSubmissionResponse> {
    const url = Endpoints.ipGetEmployerRecentSubmissions(employerId);
    return this.apiClient.getObj(RecentMemberSubmissionResponse, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateBankingInformationSubmission(
    req: CreateBankingInformationSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateBankingInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpCreateTransferInSubmission(req: MpCreateTransferInSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.mpCreateTransferInSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateTransferInSubmission(req: IpCreateTransferInSubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateTransferInSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipUpdateTransferInTransferRecord(req: IpUpdateTransferInTransferRecordRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipUpdateTransferInTransferRecord(req.id);
    return this.apiClient.putObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateBankingInformationSubmission(
    req: CreateBankingInformationSubmissionRequest
  ): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateBankingInformationSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreatePaylineAccountSubmission(req: IpCreatePaylineAccountRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreatePaylineAccountSubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipCreateLegalFiduciarySubmission(req: CreateLegalFiduciarySubmissionRequest): Observable<SubmissionCreated> {
    const url = Endpoints.ipCreateLegalFiduciarySubmission();
    return this.apiClient.postObj(SubmissionCreated, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSaveVariableBenefitConfigurationSubmission(
    req: CreateVariableBenefitConfigurationRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSaveVariableBenefitConfigurationSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitVariableBenefitConfigurationSubmission(
    req: CreateVariableBenefitConfigurationRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitVariableBenefitConfigurationSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetMemberActiveVariableBenefitConfigSubmission(
    memberId: number,
    opts = new ODataQueryOptions()
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipGetMemberActiveVariableBenefitConfigSubmission(memberId);
    return this.apiClient.getOdataObj(url, MemberSubmission, opts).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipDeleteVariableBenefitSubmission(submissionId: number): Observable<HttpResponse<any>> {
    const url = Endpoints.ipDeleteVariableBenefitSubmission(submissionId);
    return this.apiClient.deleteWithUntypedRes(url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitUpdateVariableBenefitConfigurationSubmission(
    req: UpdateVariableBenefitConfigurationSubmissionRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitUpdateVariableBenefitConfigurationSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitVariableBenefitPaymentChangeSubmission(
    req: IpVariableBenefitPaymentChangeSubmissionRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitVariableBenefitPaymentChangeSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpSubmitVariableBenefitPaymentChangeSubmission(
    req: MpVariableBenefitPaymentChangeSubmissionRequest
  ): Observable<MemberSubmission> {
    const url = Endpoints.mpSubmitVariableBenefitPaymentChangeSubmission();
    return this.apiClient.postObj(MemberSubmission, url, req).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetSubmissionsForMember(memberId: number): Observable<MemberSubmission[]> {
    const url = Endpoints.ipGetMemberSubmissions();
    const opts = new ODataQueryOptions();
    opts.setFilter(`memberId eq ${memberId}`);
    return this.apiClient.getOdata<MemberSubmission>(url, MemberSubmission, opts).pipe(
      map(response => response.value),
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public mpGetRetirementProductSubmissionAvailability(): Observable<MpRetirementProductSubmissionAvailability> {
    const url = Endpoints.mpGetRetirementProductSubmissionAvailability();
    return this.apiClient.getObj(MpRetirementProductSubmissionAvailability, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipGetRetirementProductSubmissionAvailability(
    memberId: number
  ): Observable<IpRetirementProductSubmissionAvailability> {
    const url = Endpoints.ipGetRetirementProductSubmissionAvailability(memberId);
    return this.apiClient.getObj(IpRetirementProductSubmissionAvailability, url).pipe(
      catchError(e => {
        const err = new CustomError(e, this.serviceName);
        return throwError(() => err);
      })
    );
  }

  public ipSubmitMonthlyPensionSetupSubmission(req: CreateMonthlyPensionSetupRequest): Observable<MemberSubmission> {
    const url = Endpoints.ipSubmitMonthlyPensionSetupSubmission();
    return this.apiClient
      .postObj(MemberSubmission, url, req)
      .pipe(catchError(e => throwError(() => new CustomError(e, this.serviceName))));
  }
}
